/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {
  Account,
  Link,
  PegBehavior
} from '@ion/core/src/types/entities';
import {FormState} from '@ion/dls/src/hooks/useFormState';

export const path  = 'admin-pegbehaviors:editors.forms.fields';

export enum PegBehaviorFormFields {
  Name = 'name',
  Description = 'description',
  Inherited = 'inherited',
  PegEvents = 'pegEvents',
  Status = 'status',
  AccumCounts = 'accumCounts',
  PegAccumulators = 'pegAccumulators',
  ConfigurationHeaders = 'configurationHeaders'
}

export const accumulatorCounts = [
  {value: 16, label: '16'},
  {value: 32, label: '32'},
  {value: 64, label: '64'},
  {value: 128, label: '128'},
  {value: 210, label: '210'},
  {value: 256, label: '256'}
];

export const DEFAULT_ACCUMULATOR_COUNT = 16;

export const PAGE_SIZE = 100;

export const PAGE_ID_COUNT = 3;

export enum PB_STEPS {
  GROUP_INFO = 0,
  EVENT_CODE = 1,
  ACCUMULATORS = 2,
  CONFIG_PB_GRID = 3
};

/**
 * Get PEG Behavior Form Data
 */
export const getFormData = (pegBehavior: PegBehavior, account: Account) => {
  return {
    id        : pegBehavior?.id || '',
    account   : account,
    inherited : false,
    status    : pegBehavior?.status || 'Disabled',
    version   : pegBehavior?.version,
    pegEvents : pegBehavior?.pegEvents || []
  };
};

/**
 * Handle Validation on click of next button.
 */
export const validateOnNextNavigation = ({
  step,
  isValidPBName,
  isPegNameExist,
  isValidAccumulators,
  isValidEvents,
  isValidCFactor,
  isDuplicateItem,
  isDuplicateAccItem
}) => {
  const nextStep = step < PB_STEPS.CONFIG_PB_GRID ? step + 1 : step;

  if (step === PB_STEPS.GROUP_INFO && isValidPBName) {
    if (!isPegNameExist) {
      return nextStep;
    }
  }

  if (step === PB_STEPS.EVENT_CODE && !isDuplicateItem && isValidEvents) {
    return nextStep;
  }

  if (step === PB_STEPS.ACCUMULATORS && !isDuplicateAccItem && isValidAccumulators && isValidCFactor) {
    return nextStep;
  }
};

/**
 * Handle validation on click of each steppers.
 */
export const validateOnStepperNavigation = ({
  step,
  activeStep,
  isValidPBName,
  isPegNameExist,
  isValidAccumulators,
  isValidEvents,
  isDuplicateItem,
  isDuplicateAccItem,
  pegLabels,
  isPegBehaviorLoading = false,
  isLoading = false,
  isAllLoading = false,
  isValidCFactor = true
}) => {
  let isValid = false;
  if (
    activeStep === PB_STEPS.GROUP_INFO &&
    !isEmpty(pegLabels) &&
    !isAllLoading &&
    step > PB_STEPS.GROUP_INFO
  ) {
    isValid = isValidPBName;
    if (isPegNameExist) {
      isValid = false;
    }
  }

  if (step === PB_STEPS.ACCUMULATORS) {
    return isValidEvents && !isDuplicateItem && !isLoading && !isAllLoading;
  }

  if (step === PB_STEPS.CONFIG_PB_GRID) {
    return (
      isValidEvents &&
      isValidAccumulators &&
      !isDuplicateItem &&
      !isDuplicateAccItem &&
      isValidCFactor &&
      !isLoading &&
      !isAllLoading &&
      isPegBehaviorLoading
    );
  }
  return isValid;
};

// Builds the initial form state for the Peg Behavior form dialogs.
export const getInitialPegBehaviorFormState = (): FormState => ({
  [PegBehaviorFormFields.Name] : {
    name     : PegBehaviorFormFields.Name,
    required : true
  },
  [PegBehaviorFormFields.Description] : {
    name     : PegBehaviorFormFields.Description,
    required : false
  },
  [PegBehaviorFormFields.PegEvents] : {
    name     : PegBehaviorFormFields.PegEvents,
    value    : [],
    required : false
  },
  [PegBehaviorFormFields.PegAccumulators] : {
    name     : PegBehaviorFormFields.PegAccumulators,
    value    : [],
    required : false
  },
  [PegBehaviorFormFields.AccumCounts] : {
    name     : PegBehaviorFormFields.AccumCounts,
    value    : DEFAULT_ACCUMULATOR_COUNT,
    required : false
  },
  [PegBehaviorFormFields.Inherited] : {
    name     : PegBehaviorFormFields.Inherited,
    required : false,
    value    : false
  },
  [PegBehaviorFormFields.Status] : {
    name     : PegBehaviorFormFields.Status,
    required : false,
    value    : 'Disabled'
  },
  [PegBehaviorFormFields.ConfigurationHeaders] : {
    name     : PegBehaviorFormFields.ConfigurationHeaders,
    required : false,
    value    : []
  }
});

// Serializes the form state into an API-consumable PEG Behavior
export const serialize = (formState: FormState, account: Link): PegBehavior => {
  const {pegBehavior, ...rest} = formState;
  const serialized: PegBehavior = getFormData(pegBehavior, account);
  for (const [key, {value}] of Object.entries(rest)) {
    switch (key) {
      case PegBehaviorFormFields.Name :
        serialized.name = value;
        break;
      case PegBehaviorFormFields.Description :
        serialized.description = value;
        break;
      case PegBehaviorFormFields.Status :
        serialized.status = value;
        break;
      case PegBehaviorFormFields.Inherited :
        serialized.inherited = value;
        break;
      default :
        if (value || value === false) {
          serialized[key] = value;
        }
        break;
    }
  }
  return serialized;
};

// Deserializes the PEG Label into it's respective form fields state
export const deserialize = (pegBehavior: PegBehavior) => {
  if (pegBehavior) {
    const initialState = {
      pegBehavior,
      [PegBehaviorFormFields.Name] : {
        name  : PegBehaviorFormFields.Name,
        value : pegBehavior.name
      },
      [PegBehaviorFormFields.Description] : {
        name     : PegBehaviorFormFields.Description,
        value    : pegBehavior.description,
        required : false
      },
      [PegBehaviorFormFields.Status] : {
        name     : PegBehaviorFormFields.Status,
        required : false,
        value    : pegBehavior.status || 'Disabled'
      },
      [PegBehaviorFormFields.Inherited] : {
        name  : PegBehaviorFormFields.Inherited,
        value : pegBehavior.inherited
      }
    };
    return initialState;
  }
};
